import { ReactElement, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { person } from "ionicons/icons";
import styled from "styled-components";
import Button from "../../../atoms/Button";
import Gap from "../../../atoms/Gap";
import {
  LOGOECEVE,
  LOGOINSERM,
  LOGOPARISUNI,
  LOGORECHERCHE,
  LOGOSANTE
} from "../../../../constants/images";
import Title from "../../../atoms/Title";
import AnonymousData from "../../../molecules/Home/Consent/DisplayAnonymousData";
import VeriticalCenter from "../../../atoms/VerticalCenter";
import Container from "../../../atoms/Container";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { UserStatus, WelcompeStepEnum } from "../../../../types/welcome";
import { black } from "../../../../theme/theme";
import { useResonanceLayout } from '../../ResonanceContext/ResonanceLayoutContext';
import EmailLoginFields from "../../../molecules/Home/Login/EmailLoginFields";
import SecretQuestionsForm from "../LoginJourney/SecretQuestionsForm";
import { IdentificationMethodEnum, isPasswordSignInMethod } from "../../../../types/profile";
import useToast from "../../../../hooks/useToast";
import { useStudySlug } from "../../../../hooks/useStudySlug";
import AuthContext from "../../../../contexts/AuthContext";
import { useConfig } from "../../../../contexts/ConfigContext";
import { login } from "../../../../services/user.service";
import { useIonRouter } from '@ionic/react';
import {
  getPasswordSignInPlaceholder,
  getRoleAccessEnabledIdentifierKeys
} from "../../../../helper/roleAccess";





const DisplayHasAccount = (): ReactElement | null => {
  const { t, i18n } = useTranslation();

  const { welcomeData, setCurrentStep, createUser } = useHomeContext();
  const userStatus = welcomeData.invitations?.to_user?.user_status as string;

  const study_slug = useStudySlug();
  const { startSession, user, isAuthenticated } = useContext(AuthContext);
  const present = useToast();
  const studyId = welcomeData.id;
  const { selectedRole } = useConfig();
  // Use the role chosen from the entry URL (subject / investigator / intervenor),
  // not always roles[0] (the subject). Falls back to roles[0] until selection settles.
  const role = selectedRole ?? welcomeData?.roles?.[0];
  const roleId = role?.id;
  const ionRouter = useIonRouter();
  const invitation_token = new URLSearchParams(window.location.search).get("invitation");
  const isActive = userStatus === UserStatus.ACTIVE;
  const invitSignOnly = !role?.attributes?.can_sign_up_only_with_invitation || invitation_token;

  const access = role.access;
  const method = access.signInMode;
  const identification_questions = access.secretQuestions;
  const enabledIdentifierKeys = useMemo(
    () => getRoleAccessEnabledIdentifierKeys(access),
    [access]
  );
  const isPasswordMethod = isPasswordSignInMethod(method);
  const isEmailOnlyPasswordSignIn = isPasswordMethod
    && enabledIdentifierKeys.length === 1
    && enabledIdentifierKeys[0] === 'email';
  const loginPlaceholder = useMemo(
    () => getPasswordSignInPlaceholder(access, t, i18n.resolvedLanguage ?? i18n.language),
    [access, i18n.language, i18n.resolvedLanguage, t]
  );
  const { setResonanceLayout } = useResonanceLayout();


  // useEffect(() => {
  //   // mark this page as “welcome” while it is mounted
  //   if (!isAuthenticated){
  //     setResonanceLayout({ title: "", disableNav:true, welcomePage: true, disableHeader: false, backgroundColor: "white" });
  //   }
    

  //   // ⬇️ this runs *before* the component actually unmounts
  //   return () => {
  //     if (isAuthenticated){
  //       setResonanceLayout({ welcomePage: false, disableHeader: true, disableNav: false, backgroundColor: "white" });
  //     }
      
  //   };
  // }, []);               // ← same dependency list

  useEffect(() => {
    if (!isAuthenticated) {
      setResonanceLayout({
        disableHeader: false,
        disableNav: true,
        backgroundColor: "white",
        title: "",
        welcomePage: true,
      });
      return;
    }

    setResonanceLayout({
      disableHeader: true,
      disableNav: false,
      backgroundColor: "white",
      title: "",
      welcomePage: false,
    });
  }, [isAuthenticated, setResonanceLayout]);

  // if (!welcomePage) return null;

  const handleConnect = async (body: any) => {
    try {
      const { data } = await login(studyId, roleId, { ...body, invitation_token });
      await startSession(data.token);
      ionRouter.push(`/${study_slug}/tabs/home`, 'root', 'replace', { unmount: true });
    } catch (e) {
      const status = (e as AxiosError).response?.status;
      present(
        status === 429
          ? t("home_login_too_many_attempts")
          : status === 401
            ? t("home_login_incorrect")
            : t("common_server_error_title")
      );
      console.error(e);
    }
  };

  const onAccountClick = (): void => setCurrentStep(WelcompeStepEnum.LOGIN);

  const onNewClick = (): void => {
    createUser(role);
  };


  


  







  return (
    <div style={{ marginTop: "20px" }}>
    <Container>
      <VeriticalCenter style={{ justifyContent: "space-around", minHeight: "80vh", maxHeight: "1000px",     // prevents excessive spacing on big screens
    margin: "0 auto" }}>
        <ScreenTitle>{t("home_welcome")}</ScreenTitle>
        <Description>{t("home_description")}</Description>

        <Gap size="2.5%" />
        {/* <Title>{t("home_login_subtitle")}</Title> */}

        {isPasswordMethod && (
          <EmailLoginFields
            onConnect={handleConnect}
            withNickName={!isEmailOnlyPasswordSignIn}
            loginPlaceholder={loginPlaceholder}
          />
        )}
        {method === IdentificationMethodEnum.SECRET_QUESTIONS && (
          <SecretQuestionsForm
            onConnect={handleConnect}
            secretQuestions={identification_questions}
          />
        )}

         <Gap mt="20px" />
        
        {(invitSignOnly && !isActive) && (
          <>
            
            <Gap size="2.5%" />
            {/* <Title>{t("home_new")}</Title> */}
            <LoginButton onClick={onNewClick} icon={person}>
              {t("home_has_account_false")}
            </LoginButton>
          </>
        )}

        {/* <RegistredButton onClick={onAccountClick} icon={enterOutline}>
          {t("home_has_account_true")}
        </RegistredButton> */}

        <Gap size="5%" />
        {/* <AnonymousData /> */}

        <LogoContainer>
          <Logo src={LOGOECEVE} />
          <Logo src={LOGOINSERM} />
          <Logo src={LOGOPARISUNI} />
          <Logo src={LOGOSANTE} />
          <Logo src={LOGORECHERCHE} />
        </LogoContainer>
      </VeriticalCenter>
    </Container>
    </div>
  );
};

const ScreenTitle = styled.h1`
  text-align: center;
  color: #393939;
  font-weight: bold;
  margin-bottom: 20px;
`;

const Description = styled.div`
  font-size: 18px;
  text-align: center;
  margin: 0 5% 0 5%;
`;

const LoginButton = styled(Button)`
  background-color: white;
  // color: ${(props) => props.theme.primary.contrast};
  color: #4285F4;
  margin: 0 auto;
  padding: 0 50px;
  border: 2px solid #4285F4;
`;

const RegistredButton = styled(Button)`
  background-color: ${(props) => props.theme.primary.background};
  color: ${black};
  margin: 0 auto;
  padding: 0 50px;
`;

const Logo = styled.img`
  height: auto;
  width: 8%;

  @media (min-width: 320px) and (max-width: 480px) {
    width: 15%;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  height: 100px;
`;

export default DisplayHasAccount;
